import React from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  CloseButton,
} from "@headlessui/react";
import { OrderType } from "@/types";
import refreshMenu from "@/dataLayer/refreshMenu";

type OrderTypeDropdownType = {
  orderType: OrderType;
} & {
  setOrderType: any;
  availableOrderTypes: any;
};

export const OrderTypeDropdown = ({
  orderType,
  setOrderType,
  availableOrderTypes,
}: OrderTypeDropdownType) => {
  const enabledOrderTypes = availableOrderTypes.filter(
    (ot: { enabled: any }) => ot.enabled
  );

  const deliveryOrderType = availableOrderTypes.find(
    (ot: { type: string }) => ot.type === "DELIVERY"
  );
  const deliveryEnabled = deliveryOrderType?.enabled;

  const curbsideOrderType = availableOrderTypes.find(
    (ot: { type: string }) => ot.type === "CURBSIDE"
  );
  const curbsideEnabled = curbsideOrderType?.enabled;

  const inStoreOrderType = availableOrderTypes.find(
    (ot: { type: string }) => ot.type === "PICKUP"
  );
  const inStoreEnabled = inStoreOrderType?.enabled;

  return (
    <Popover className="relative">
      <PopoverButton
        className={[
          "flex",
          "items-center",
          "gap-x-2",
          "w-full",
          "leading-6",
          "text-gray-900",
          "hover:bg-gray-50",
          "disabled:hover:bg-transparent",
          "disabled:cursor-default",
        ].join(" ")}
        disabled={enabledOrderTypes.length < 2}
      >
        <input
          type="text"
          id="order_type_input"
          className="block px-2.5 h-[58px] py-2.5 w-full text-gray-900 bg-transparent rounded-[4px] border border-black hover:border-black focus:outline-none focus:ring-1 focus:ring-blue peer cursor-pointer disabled:border-transparent disabled:cursor-default"
          value={orderDisplayValue(orderType)}
          onChange={(e) => setOrderType(e.target.value)}
          disabled={enabledOrderTypes.length < 2}
        />
        <label
          htmlFor="order_type_input"
          className="absolute text-md text-[#727272] dark:text-[#727272] duration-300 transform -translate-y-5 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-5 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
        >
          Order type
        </label>
      </PopoverButton>
      <PopoverPanel
        transition
        className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="p-3">
          {inStoreEnabled && (
            <CloseButton
              onClick={() => {
                refreshMenu();
                setOrderType(OrderType.PICKUP);
              }}
              className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 pr-1 leading-6 hover:bg-gray-50"
            >
              <p className="text-left w-full text-[15px]">In-store pickup</p>
            </CloseButton>
          )}
          {curbsideEnabled && (
            <CloseButton
              onClick={() => {
                refreshMenu();
                setOrderType(OrderType.CURBSIDE);
              }}
              className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50"
            >
              <p className="text-left w-full text-[15px]">Curbside Pickup</p>
            </CloseButton>
          )}
          {deliveryEnabled && (
            <CloseButton
              onClick={() => {
                refreshMenu();
                setOrderType(OrderType.DELIVERY);
              }}
              className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50"
            >
              <p className="text-[15px]">Delivery</p>
            </CloseButton>
          )}
        </div>
      </PopoverPanel>
    </Popover>
  );
};

const orderDisplayValue = (orderType: string) => {
  if (orderType === OrderType.PICKUP) {
    return "In-store pickup";
  } else if (orderType === OrderType.CURBSIDE) {
    return "Curbside pickup";
  } else if (orderType === OrderType.DELIVERY) {
    return "Delivery";
  }
  return "In-store pickup";
};
